import React, { useEffect, useState } from "react";

function SettingsPanel({ isOpen, onClose, language, toggleLanguage, theme, toggleTheme }) {
  const [selectedTab, setSelectedTab] = useState("language");
  const [animationState, setAnimationState] = useState("closed");

  useEffect(() => {
    if (isOpen) {
      setAnimationState("opening");
      const timer = setTimeout(() => {
        setAnimationState("open");
      }, 50);
      return () => clearTimeout(timer);
    } else {
      setAnimationState("closing");
      const timer = setTimeout(() => {
        setAnimationState("closed");
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (animationState === "closed" && !isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className={`fixed inset-0 z-40 bg-black/20 backdrop-blur-sm transition-all duration-300 ${
          animationState === "open" ? "opacity-100" : "opacity-0"
        }`}
        onClick={onClose}
      ></div>

      {/* Settings Panel */}
      <div 
        className={`fixed top-16 right-4 z-50 w-80 font-mono transition-all duration-300 ${
          animationState === "open" || animationState === "opening" 
            ? "opacity-100 translate-y-0 scale-100" 
            : "opacity-0 -translate-y-4 scale-95"
        }`}
      >
        <div className="relative bg-slate-900/95 border border-cyan-400/30 rounded-lg overflow-hidden backdrop-blur-md shadow-2xl shadow-cyan-400/10">
          {/* Header */}
          <div className="px-4 py-3 border-b border-cyan-400/20 flex justify-between items-center bg-cyan-400/5">
            <div className="text-cyan-400 flex items-center">
              <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse mr-2"></div>
              <span className="text-sm font-semibold tracking-wider">SETTINGS</span>
            </div>
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-cyan-400 transition-colors p-1 rounded hover:bg-cyan-400/10"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>

          {/* Tab Navigation */}
          <div className="flex relative">
            <div className={`absolute bottom-0 h-0.5 bg-cyan-400 transition-all duration-300 ${
              selectedTab === "language" ? "left-0 w-1/2" : "left-1/2 w-1/2"
            }`}></div>
            
            <button
              className={`flex-1 py-3 text-xs font-semibold tracking-wider transition-colors ${
                selectedTab === "language" 
                  ? "text-cyan-400 bg-cyan-400/10" 
                  : "text-gray-400 hover:text-cyan-300 hover:bg-gray-400/5"
              }`}
              onClick={() => setSelectedTab("language")}
            >
              LANGUAGE
            </button>
            <button
              className={`flex-1 py-3 text-xs font-semibold tracking-wider transition-colors ${
                selectedTab === "theme" 
                  ? "text-cyan-400 bg-cyan-400/10" 
                  : "text-gray-400 hover:text-cyan-300 hover:bg-gray-400/5"
              }`}
              onClick={() => setSelectedTab("theme")}
            >
              DISPLAY
            </button>
          </div>

          {/* Content */}
          <div className="p-4 min-h-[200px]">
            {selectedTab === "language" && (
              <div className="space-y-3">
                <div className="text-xs text-cyan-300/80 mb-4 flex items-center">
                  <span className="w-1 h-1 bg-cyan-400 rounded-full mr-2"></span>
                  SELECT LANGUAGE
                </div>
                
                {[
                  { code: "en", name: "English", flag: "/images/united-states.png" },
                  { code: "id", name: "Bahasa Indonesia", flag: "/images/indonesia.png" }
                ].map((lang) => {
                  const isActive = (lang.code === "en" && language === "en") || (lang.code === "id" && language !== "en");
                  
                  return (
                    <button
                      key={lang.code}
                      onClick={toggleLanguage}
                      className={`flex items-center justify-between w-full p-3 rounded-lg transition-all group ${
                        isActive 
                          ? "bg-cyan-400/20 border border-cyan-400/50 shadow-cyan-400/20 shadow-sm" 
                          : "bg-slate-800/50 border border-cyan-400/10 hover:border-cyan-400/30 hover:bg-slate-800/70"
                      }`}
                    >
                      <div className="flex items-center">
                        <div className="w-8 h-8 rounded-lg overflow-hidden border border-cyan-400/30 mr-3 relative group-hover:border-cyan-400/50 transition-colors">
                          <img
                            src={lang.flag}
                            alt={lang.name}
                            loading="lazy"
                            decoding="async"
                            className="w-full h-full object-cover"
                          />
                          {isActive && (
                            <div className="absolute inset-0 bg-cyan-400/20"></div>
                          )}
                        </div>
                        <span className={`text-sm ${isActive ? "text-cyan-400 font-semibold" : "text-gray-300"}`}>
                          {lang.name}
                        </span>
                      </div>
                      
                      {isActive && (
                        <div className="flex items-center">
                          <div className="w-4 h-4 bg-cyan-400 rounded-full flex items-center justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-slate-900" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {selectedTab === "theme" && (
              <div className="space-y-4">
                <div className="text-xs text-cyan-300/80 mb-4 flex items-center">
                  <span className="w-1 h-1 bg-cyan-400 rounded-full mr-2"></span>
                  APPEARANCE
                </div>
                
                <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-cyan-400/10">
                  <div className="flex items-center">
                    <span className="text-sm text-gray-300 mr-3">🌙</span>
                    <span className="text-sm text-gray-300">Dark Mode</span>
                  </div>
                  
                  <button 
                    onClick={toggleTheme}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:ring-offset-2 focus:ring-offset-slate-900 ${
                      theme === 'dark' ? 'bg-cyan-400' : 'bg-gray-600'
                    }`}
                  >
                    <span
                      className={`${
                        theme === 'dark' ? 'translate-x-6' : 'translate-x-1'
                      } inline-block h-4 w-4 transform rounded-full bg-white transition`}
                    />
                  </button>
                </div>
                
                <div className="bg-slate-800/30 p-3 rounded-lg border border-yellow-400/20 text-xs text-gray-400">
                  <div className="flex items-center mb-2">
                    <span className="w-2 h-2 bg-yellow-400 rounded-full mr-2 animate-pulse"></span>
                    <span className="text-yellow-400 font-semibold">NOTICE</span>
                  </div>
                  <p>Theme switching feature is coming soon in v2.0</p>
                </div>
              </div>
            )}
          </div>

          {/* Decorative Elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-cyan-400/40"></div>
            <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-cyan-400/40"></div>
            <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-cyan-400/40"></div>
            <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-cyan-400/40"></div>
          </div>
        </div>
      </div>
    </>
  );
}

export default SettingsPanel;